import { SearchMatchType, SearchType } from '@aibees/elephant/types';
import { sortBy } from 'lodash-es';
import { KeywordSources } from '../../search-table-company/hooks/types';
import {
  PEOPLE_KEYWORD_SOURCES_CUSTOM_SORT_ORDER,
  PeopleKeywordSourceLabels,
  PeopleKeywordSources,
} from '../../search-table-people/hooks/types';
import {
  FilterBadgeType,
  FilterCategory,
  SearchFilter,
  SearchFilterBase,
  SearchFilterOption,
  SearchFilterType,
} from '../types';

export default function keywordsFilterConfig(
  searchType: SearchType,
  employees?: boolean
): SearchFilterBase {
  const isCompanySearch = searchType === SearchType.Company;
  const companiesKeywords: SearchFilter = {
    name: 'Companies',
    type: SearchFilterType.NONE,
    icon: 'business',
    index: 3,
    key: 'companies_keywords',
    field: 'companies_keywords',
    metadata: {
      tabs: {
        icon: 'business',
        iconColor: 'primary',
      },
    },
    utils: {
      tags: {
        config: {
          showAsCard: true,
          tagsCardTitle: 'Company Keywords',
        },
      },
    },
    sections: { main: 2, more: 1 },
    open: true,
    mainItem: true,
    layer: 3,
    items: [
      {
        index: 0,
        type: SearchFilterType.GROUP_WRAPPER,
        name: '',
        icon: undefined,
        metadata: {},
        sections: { main: 2, more: 1 },
        layer: 3,
        items: [
          {
            name: 'Include',
            type: SearchFilterType.TEXT_FIELD,
            icon: undefined,
            index: 0,
            key: 'companies_include_keywords',
            field: 'companies_include_keywords',
            metadata: {
              color: 'primary',
              preset: 'companies_include_keywords',
              placeholder: 'Enter Keywords',
              advancedButtonVisible: true,
              dialog: {
                title: 'Bulk Include Keywords',
                subTitleItemLabel: 'Keywords',
                subTitleItemLabelPlural: 'Keywords',
                icon: 'keyboard',
                textArea: {
                  placeholder: 'Digital Transformation, SaaS, Logistics, ...',
                },
              },
            },
            open: true,
            layer: 4,
            advance: [
              {
                name: 'Include Search Settings',
                type: SearchFilterType.CHECK_BOX,
                index: 0,
                key: 'companies_include_keywords_sources',
                field: 'companies_include_keywords_sources',
                metadata: {
                  availability: {
                    required: true,
                    info: {
                      Company:
                        'In order to Adjust the Include Keyword Source Setting you need to enter at least one keyword in to the Include Keyword Search.',
                      People: '',
                      Employees: '',
                      BuyingTeam: '',
                    },
                  },
                  checkbox: {
                    defaultSelected: true,
                    showSelectAllOption: true,
                    deselectAllExceptionKeys: [KeywordSources.DESCRIPTION],
                    showOnlyButton: true,
                    itemSx: {
                      paddingX: 0,
                    },
                  },
                  color: 'primary',
                  hideTags: true,
                  skipUrlParams: true,
                },
                config: { labelKey: 'label', valueKey: 'value' },
                open: true,
                options: companyKeywordsSources,
              },
            ],
          },
          {
            name: 'Exclude',
            type: SearchFilterType.TEXT_FIELD,
            icon: undefined,
            index: 1,
            key: 'companies_exclude_keywords',
            field: 'companies_exclude_keywords',
            metadata: {
              color: 'error',
              icon: 'block',
              preset: 'companies_exclude_keywords',
              placeholder: 'Enter Keywords',
              advancedButtonVisible: true,
              dialog: {
                title: 'Bulk Exclude Keywords',
                subTitleItemLabel: 'Keywords',
                subTitleItemLabelPlural: 'Keywords',
                icon: 'keyboard',
                textArea: {
                  placeholder: 'Digital Transformation, SaaS, Logistics, ...',
                },
              },
            },
            open: true,
            layer: 4,
            advance: [
              {
                name: 'Exclude Search Settings',
                type: SearchFilterType.CHECK_BOX,
                index: 1,
                key: 'companies_exclude_keywords_sources',
                field: 'companies_exclude_keywords_sources',
                metadata: {
                  availability: {
                    required: true,
                    info: {
                      Company:
                        'In order to Adjust the Exclude Keyword Source Setting you need to enter at least one keyword in to the Include Keyword Search.',
                      People: '',
                      Employees: '',
                      BuyingTeam: '',
                    },
                  },
                  checkbox: {
                    defaultSelected: true,
                    showSelectAllOption: true,
                    deselectAllExceptionKeys: [KeywordSources.DESCRIPTION],
                    showOnlyButton: true,
                    itemSx: {
                      paddingX: 0,
                    },
                  },
                  color: 'error',
                  icon: 'block',
                  hideTags: true,
                  skipUrlParams: true,
                },
                config: { labelKey: 'label', valueKey: 'value' },
                open: true,
                options: companyKeywordsSources,
              },
            ],
          },
          {
            name: 'Include (AND)',
            type: SearchFilterType.TEXT_FIELD,
            icon: undefined,
            index: 2,
            key: 'companies_all_of_keywords',
            field: 'companies_all_of_keywords',
            metadata: {
              color: 'warning',
              icon: 'add-task',
              preset: 'companies_all_of_keywords',
              placeholder: 'Enter Keywords',
              advancedButtonVisible: true,
              dialog: {
                title: 'Bulk Include (AND) Keywords',
                subTitleItemLabel: 'Keywords',
                subTitleItemLabelPlural: 'Keywords',
                icon: 'keyboard',
                textArea: {
                  placeholder: 'Digital Transformation, SaaS, Logistics, ...',
                },
              },
            },
            open: true,
            layer: 4,
            advance: [
              {
                name: 'Include (AND) Search Settings',
                type: SearchFilterType.CHECK_BOX,
                index: 2,
                key: 'companies_all_of_keywords_sources',
                field: 'companies_all_of_keywords_sources',
                metadata: {
                  availability: {
                    required: true,
                    info: {
                      Company:
                        'In order to Adjust the Include (AND) Keyword Source Setting you need to enter at least one keyword in to the Include Keyword Search.',
                      People: '',
                      Employees: '',
                      BuyingTeam: '',
                    },
                  },
                  checkbox: {
                    defaultSelected: true,
                    showSelectAllOption: true,
                    deselectAllExceptionKeys: [KeywordSources.DESCRIPTION],
                    showOnlyButton: true,
                    itemSx: {
                      paddingX: 0,
                    },
                  },
                  color: 'primary',
                  hideTags: true,
                  skipUrlParams: true,
                },
                config: { labelKey: 'label', valueKey: 'value' },
                open: true,
                options: companyKeywordsSources,
              },
            ],
          },
        ],
      },
    ],
  };
  const peopleKeywords: SearchFilter = {
    name: 'People',
    type: SearchFilterType.NONE,
    index: 2,
    key: 'people_keywords',
    field: 'people_keywords',
    metadata: {
      tabs: {
        icon: 'people',
        iconColor: 'success',
      },
    },
    utils: {
      tags: {
        config: {
          showAsCard: true,
          tagsCardTitle: 'Contacts Keywords',
        },
      },
    },
    sections: { main: 2, more: 1 },
    open: true,
    mainItem: true,
    layer: 3,
    items: [
      {
        index: 0,
        type: SearchFilterType.GROUP_WRAPPER,
        name: '',
        icon: undefined,
        metadata: {},
        sections: { main: 2, more: 1 },
        layer: 3,
        items: [
          {
            name: 'Include',
            type: SearchFilterType.TEXT_FIELD,
            icon: undefined,
            index: 0,
            key: 'people_include_keywords',
            field: 'people_include_keywords',
            metadata: {
              color: 'primary',
              preset: 'people_include_keywords',
              placeholder: 'Enter Keywords',
              advancedButtonVisible: true,
              dialog: {
                title: 'Bulk Include Keywords',
                subTitleItemLabel: 'Keywords',
                subTitleItemLabelPlural: 'Keywords',
                icon: 'keyboard',
                textArea: {
                  placeholder: 'Digital Transformation, SaaS, Logistics, ...',
                },
              },
            },
            open: true,
            layer: 4,
            advance: [
              {
                name: 'Include Search Settings',
                type: SearchFilterType.CHECK_BOX,
                index: 0,
                key: 'include_people_keywords_sources',
                field: 'include_people_keywords_sources',
                metadata: {
                  availability: {
                    required: true,
                    info: {
                      Company:
                        'In order to Adjust the Include Keyword Source Setting you need to enter at least one keyword in to the Include Keyword Search.',
                      People: '',
                      Employees: '',
                      BuyingTeam: '',
                    },
                  },
                  checkbox: {
                    defaultSelected: true,
                    showSelectAllOption: true,
                    deselectAllExceptionKeys: [PeopleKeywordSources.HEADLINE],
                    showOnlyButton: true,
                    itemSx: {
                      paddingX: 0,
                    },
                  },
                  color: 'primary',
                  hideTags: true,
                  skipUrlParams: true,
                },
                config: { labelKey: 'label', valueKey: 'value' },
                open: true,
                options: peopleKeywordsSources,
              },
            ],
          },
          {
            name: 'Exclude',
            type: SearchFilterType.TEXT_FIELD,
            icon: undefined,
            index: 1,
            key: 'people_exclude_keywords',
            field: 'people_exclude_keywords',
            metadata: {
              color: 'error',
              icon: 'block',
              preset: 'people_exclude_keywords',
              placeholder: 'Enter Keywords',
              advancedButtonVisible: true,
              dialog: {
                title: 'Bulk Exclude Keywords',
                subTitleItemLabel: 'Keywords',
                subTitleItemLabelPlural: 'Keywords',
                icon: 'keyboard',
                textArea: {
                  placeholder: 'Digital Transformation, SaaS, Logistics, ...',
                },
              },
            },
            open: true,
            layer: 4,
            advance: [
              {
                name: 'Exclude Search Settings',
                type: SearchFilterType.CHECK_BOX,
                index: 1,
                key: 'people_exclude_keywords_sources',
                field: 'people_exclude_keywords_sources',
                metadata: {
                  availability: {
                    // requires: ['exclude_keywords'],
                    required: true,
                    info: {
                      Company:
                        'In order to Adjust the Exclude Keyword Source Setting you need to enter at least one keyword in to the Include Keyword Search.',
                      People: '',
                      Employees: '',
                      BuyingTeam: '',
                    },
                  },
                  checkbox: {
                    defaultSelected: true,
                    showSelectAllOption: true,
                    deselectAllExceptionKeys: [PeopleKeywordSources.HEADLINE],
                    showOnlyButton: true,
                    itemSx: {
                      paddingX: 0,
                    },
                  },
                  color: 'error',
                  icon: 'block',
                  hideTags: true,
                  skipUrlParams: true,
                },
                config: { labelKey: 'label', valueKey: 'value' },
                open: true,
                options: peopleKeywordsSources,
              },
            ],
          },
          {
            name: 'Include (AND)',
            type: SearchFilterType.TEXT_FIELD,
            icon: undefined,
            index: 2,
            key: 'people_all_of_keywords',
            field: 'people_all_of_keywords',
            metadata: {
              color: 'warning',
              icon: 'add-task',
              preset: 'people_all_of_keywords',
              placeholder: 'Enter Keywords',
              advancedButtonVisible: true,
              dialog: {
                title: 'Bulk Include (AND) Keywords',
                subTitleItemLabel: 'Keywords',
                subTitleItemLabelPlural: 'Keywords',
                icon: 'keyboard',
                textArea: {
                  placeholder: 'Digital Transformation, SaaS, Logistics, ...',
                },
              },
            },
            open: true,
            layer: 4,
            advance: [
              {
                name: 'Include (AND) Search Settings',
                type: SearchFilterType.CHECK_BOX,
                index: 2,
                key: 'people_all_of_keywords_sources',
                field: 'people_all_of_keywords_sources',
                metadata: {
                  availability: {
                    required: true,
                    info: {
                      Company:
                        'In order to Adjust the Include (AND) Keyword Source Setting you need to enter at least one keyword in to the Include Keyword Search.',
                      People: '',
                      Employees: '',
                      BuyingTeam: '',
                    },
                  },
                  checkbox: {
                    defaultSelected: true,
                    showSelectAllOption: true,
                    deselectAllExceptionKeys: [PeopleKeywordSources.HEADLINE],
                    showOnlyButton: true,
                    itemSx: {
                      paddingX: 0,
                    },
                  },
                  color: 'primary',
                  hideTags: true,
                  skipUrlParams: true,
                },
                config: { labelKey: 'label', valueKey: 'value' },
                open: true,
                options: peopleKeywordsSources,
              },
            ],
          },
        ],
      },
    ],
  };

  return {
    name: 'Keywords',
    icon: 'keyboard',
    index: 5,
    type: SearchFilterType.ROOT,
    category: isCompanySearch
      ? [FilterCategory.COMPANY_DATA]
      : [FilterCategory.COMPANY_DATA, FilterCategory.CONTACT_DATA],
    pinned: true,
    badge: FilterBadgeType.NEW,
    sections: { main: 1, source: 1 },
    items: isCompanySearch
      ? [companiesKeywords]
      : employees
      ? [peopleKeywords]
      : [
          {
            name: '',
            type: SearchFilterType.BUTTON_GROUP,
            icon: undefined,
            index: 0,
            key: '',
            field: '',
            metadata: {
              allowParallelItems: true,
              tabs: {
                defaultOpenIndex: 1,
              },
            },
            config: { labelKey: 'label', valueKey: 'value' },
            items: [
              {
                name: 'Applied Filters',
                type: SearchFilterType.APPLIED_FILTERS,
                index: 2,
                icon: undefined,
                key: 'keywords_applied_filters',
                field: 'keywords_applied_filters',
                metadata: {},
              },
              companiesKeywords,
              peopleKeywords,
            ],
          },
        ],
  };
}

const CompanyKeywordSourceLables = {
  [KeywordSources.NAME]: 'Company Name',
  [KeywordSources.KEYWORD]: 'Product & Services',
  [KeywordSources.SEO]: 'SEO Description',
  [KeywordSources.DESCRIPTION]: 'Company Description',
  [KeywordSources.INDUSTRY]: 'Industry',
};

export const CUSTOM_SORT_ORDER: KeywordSources[] = [
  KeywordSources.DESCRIPTION,
  KeywordSources.INDUSTRY,
  KeywordSources.NAME,
  KeywordSources.SEO,
  KeywordSources.KEYWORD,
];

export const companyKeywordSourceExactMatchConfig = {
  [KeywordSources.NAME]: {
    default: SearchMatchType.SMART,
    disabled: [],
  },
  [KeywordSources.KEYWORD]: {
    default: SearchMatchType.SMART,
    disabled: [],
  },
  [KeywordSources.SEO]: {
    default: SearchMatchType.SMART,
    disabled: [SearchMatchType.STRICT],
  },
  [KeywordSources.DESCRIPTION]: {
    default: SearchMatchType.SMART,
    disabled: [SearchMatchType.STRICT],
  },
  [KeywordSources.INDUSTRY]: {
    default: SearchMatchType.SMART,
    disabled: [],
  },
};

export const companyKeywordsSources = sortBy(
  Object.values(KeywordSources).map(
    (source, index) =>
      ({
        type: 'STRING',
        value: source,
        label: CompanyKeywordSourceLables[source],
        icon: null,
        index,
        selected: true,
        metadata: {
          searchMatchItems: [
            SearchMatchType.SMART,
            SearchMatchType.WORD,
            SearchMatchType.STRICT,
          ],
          searchMatchDefault:
            companyKeywordSourceExactMatchConfig[source].default,
          disabledSearchMatchItems:
            companyKeywordSourceExactMatchConfig[source].disabled,
        },
      } as SearchFilterOption)
  ),
  (item) => CUSTOM_SORT_ORDER.indexOf(item.value as KeywordSources)
);

export const peopleKeywordSourceExactMatchConfig = {
  [PeopleKeywordSources.FIRST_NAME]: {
    default: SearchMatchType.SMART,
    disabled: [],
  },
  [PeopleKeywordSources.LAST_NAME]: {
    default: SearchMatchType.SMART,
    disabled: [],
  },
  [PeopleKeywordSources.ORGANIZATION]: {
    default: SearchMatchType.SMART,
    disabled: [SearchMatchType.STRICT],
  },
  [PeopleKeywordSources.TEST_SCORE]: {
    default: SearchMatchType.SMART,
    disabled: [SearchMatchType.STRICT],
  },
  [PeopleKeywordSources.AWARD]: {
    default: SearchMatchType.SMART,
    disabled: [SearchMatchType.STRICT],
  },
  [PeopleKeywordSources.PUBLICATION]: {
    default: SearchMatchType.SMART,
    disabled: [SearchMatchType.STRICT],
  },
  [PeopleKeywordSources.SUMMARY]: {
    default: SearchMatchType.SMART,
    disabled: [SearchMatchType.STRICT],
  },
  [PeopleKeywordSources.VOLUNTEERING]: {
    default: SearchMatchType.SMART,
    disabled: [SearchMatchType.STRICT],
  },
  [PeopleKeywordSources.HEADLINE]: {
    default: SearchMatchType.SMART,
    disabled: [SearchMatchType.STRICT],
  },
  [PeopleKeywordSources.EDUCATION_DESCRIPTION]: {
    default: SearchMatchType.SMART,
    disabled: [SearchMatchType.STRICT],
  },
  [PeopleKeywordSources.COURSE]: {
    default: SearchMatchType.SMART,
    disabled: [SearchMatchType.STRICT],
  },
  [PeopleKeywordSources.PROJECTS]: {
    default: SearchMatchType.SMART,
    disabled: [SearchMatchType.STRICT],
  },
  [PeopleKeywordSources.SKILL]: {
    default: SearchMatchType.SMART,
    disabled: [],
  },
  [PeopleKeywordSources.LANGUAGE_SKILL]: {
    default: SearchMatchType.WORD,
    disabled: [SearchMatchType.SMART],
  },
  [PeopleKeywordSources.CERTIFICATION]: {
    default: SearchMatchType.SMART,
    disabled: [SearchMatchType.STRICT],
  },
  [PeopleKeywordSources.WORK_HISTORY_DESCRIPTION]: {
    default: SearchMatchType.SMART,
    disabled: [SearchMatchType.STRICT],
  },
  [PeopleKeywordSources.PATENT]: {
    default: SearchMatchType.SMART,
    disabled: [SearchMatchType.STRICT],
  },
};

export const peopleKeywordsSources = sortBy(
  Object.values(PeopleKeywordSources).map(
    (source, index) =>
      ({
        type: 'STRING',
        value: source,
        label: PeopleKeywordSourceLabels[source],
        icon: null,
        index,
        selected: true,
        metadata: {
          searchMatchItems: [
            SearchMatchType.SMART,
            SearchMatchType.WORD,
            SearchMatchType.STRICT,
          ],
          searchMatchDefault:
            peopleKeywordSourceExactMatchConfig[
              source as keyof typeof peopleKeywordSourceExactMatchConfig
            ].default,
          disabledSearchMatchItems:
            peopleKeywordSourceExactMatchConfig[
              source as keyof typeof peopleKeywordSourceExactMatchConfig
            ].disabled,
        },
      } as SearchFilterOption)
  ),
  (item) =>
    PEOPLE_KEYWORD_SOURCES_CUSTOM_SORT_ORDER.indexOf(
      item.value as PeopleKeywordSources
    )
);
